/**
 * RAG 시스템 프론트엔드 애플리케이션의 메인 컴포넌트
 * 
 * 이 컴포넌트는 React Router를 사용하여 전체 애플리케이션의 라우팅 구조를 정의합니다.
 * 사용자 인터페이스는 다음 두 가지 주요 섹션으로 구성됩니다:
 * 1. 사용자 영역: 일반 사용자를 위한 채팅 인터페이스 제공
 * 2. 관리자 영역: 지식베이스 관리, 크롤링, 문서 업로드 등 시스템 관리 기능 제공
 * 
 * 라우팅에는 권한 검사 및 접근 제어 기능이 포함되어 있습니다.
 */

import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';

// 레이아웃 컴포넌트
import MainLayout from './components/layouts/MainLayout';  // 메인 사용자 레이아웃
import AdminLayout from './components/layouts/AdminLayout';  // 관리자 레이아웃

// 사용자 페이지
import ChatPage from './pages/user/ChatPage';  // 챗봇 대화 페이지
import CasesPage from './pages/user/CasesPage';  // 적용 사례 페이지
import HomePage from './pages/HomePage';  // 메인 홈페이지

// 관리자 페이지
import CrawlPage from './pages/admin/CrawlPage';  // 웹 크롤링 관리 페이지
import DocumentsPage from './pages/admin/DocumentsPage';  // 문서 업로드/관리 페이지
import DatabasePage from './pages/admin/DatabasePage';  // 외부 DB 연동 페이지
import KnowledgeBasePage from './pages/admin/KnowledgeBasePage';  // 지식베이스 관리 페이지
import ModelsPage from './pages/admin/ModelsPage';  // 모델 설정 페이지
import ApiToolsPage from './pages/admin/ApiToolsPage';  // API 도구 관리 페이지
import AdminUsersPage from './pages/admin/AdminUsersPage';  // 관리자 계정 관리 페이지
import FaqPage from './pages/admin/FaqPage';  // 큐레이션된 답변 관리 페이지
import ChatHistoryPage from './pages/admin/ChatHistoryPage';  // 질의응답 내역 페이지

// 투어 가이드 컴포넌트
import TourGuide from './components/TourGuide';

/**
 * App 컴포넌트 - 애플리케이션의 메인 진입점
 * 
 * 인증 상태와 권한을 관리하고, 이에 따라 접근 가능한 라우트를 제어합니다.
 * 현재는 개발 편의를 위해 인증과 관리자 권한이 기본적으로 부여됩니다.
 * 실제 배포 환경에서는 적절한 인증 및 권한 체계로 대체되어야 합니다.
 * 
 * @returns {JSX.Element} 라우팅이 설정된 전체 애플리케이션 UI
 */
const App = () => {
  // 인증 상태 관리 (개발 편의상 기본값 true)
  const [isAuthenticated, setIsAuthenticated] = useState(true); // 개발을 위해 기본값 true
  const [isAdmin, setIsAdmin] = useState(true); // 개발을 위해 기본값 true

  /**
   * 관리자 권한이 필요한 라우트를 위한 보호 컴포넌트
   * 관리자가 아닌 사용자는 홈 페이지로 리다이렉트됩니다.
   * 
   * @param {object} props - 컴포넌트 속성
   * @param {JSX.Element} props.children - 보호할 컴포넌트/페이지
   * @returns {JSX.Element} 권한이 있으면 자식 컴포넌트, 없으면 리다이렉트
   */
  const AdminRoute = ({ children }) => {
    return isAuthenticated && isAdmin ? children : <Navigate to="/" />;
  };

  /**
   * 인증이 필요한 라우트를 위한 보호 컴포넌트
   * 인증되지 않은 사용자는 홈 페이지로 리다이렉트됩니다.
   * 
   * @param {object} props - 컴포넌트 속성
   * @param {JSX.Element} props.children - 보호할 컴포넌트/페이지
   * @returns {JSX.Element} 인증된 사용자면 자식 컴포넌트, 아니면 리다이렉트
   */
  const ProtectedRoute = ({ children }) => {
    return isAuthenticated ? children : <Navigate to="/" />;
  };

  return (
    // 전체 애플리케이션 컨테이너
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* 투어 가이드 컴포넌트 */}
      <TourGuide />
      
      <Routes>
        {/* 사용자 경로 - 일반 사용자용 UI */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<HomePage />} />
          <Route path="chat" element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          } />
          <Route path="cases" element={<CasesPage />} />
        </Route>

        {/* 관리자 경로 - 시스템 관리용 UI */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }>
          {/* 관리자 홈 - 크롤링 페이지로 리다이렉트 */}
          <Route index element={<Navigate to="/admin/crawl" replace />} />
          <Route path="crawl" element={<CrawlPage />} />  {/* 웹 크롤링 관리 */}
          <Route path="documents" element={<DocumentsPage />} />  {/* 문서 관리 */}
          <Route path="database" element={<DatabasePage />} />  {/* DB 연동 */}
          <Route path="knowledge-base" element={<KnowledgeBasePage />} />  {/* 지식베이스 관리 */}
          <Route path="faq" element={<FaqPage />} />  {/* 큐레이션된 답변 관리 */}
          <Route path="chat-history" element={<ChatHistoryPage />} />  {/* 질의응답 내역 */}
          <Route path="models" element={<ModelsPage />} />  {/* 모델 설정 */}
          <Route path="api-tools" element={<ApiToolsPage />} />  {/* API 도구 관리 */}
          <Route path="admin-users" element={<AdminUsersPage />} />  {/* 관리자 계정 관리 */}
        </Route>

        {/* 404 페이지 - 존재하지 않는 경로는 홈으로 리다이렉트 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
};

export default App; 